import React, { useEffect, useState } from 'react';
import Modal from './Modal';
import './Games.css'; // Ensure this file exists and contains relevant styles

const GameList = () => {
  const [machines, setMachines] = useState([]);
  const [count, setCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedMachine, setSelectedMachine] = useState(null);
  const [sortBy, setSortBy] = useState('manufacturer');
  const [sortOrder, setSortOrder] = useState('asc'); // 'asc' or 'desc'

  // Config option: 'opdb', 'ipdb', or 'both'
  const imageSource = 'both';

  useEffect(() => {
    const fetchMachineDetails = async () => {
      try {
        // Pinball Map requires an api_token that has to stay server-side, so this
        // goes through our own /api/machines proxy (functions/api/machines.js).
        const response = await fetch('/api/machines');
        const data = await response.json();

        if (!response.ok) {
          throw new Error(data.error || `Request failed (${response.status})`);
        }

        const machines = data.machines || [];

        // Fetch additional details from OPDB API for each machine
        const machineDetails = await Promise.all(
          machines.map(async (machine) => {
            let image = '';

            if (imageSource === 'opdb' || imageSource === 'both') {
              try {
                const opdbResponse = await fetch(`https://opdb.org/api/machines/${machine.opdb_id}?api_token=SvlXTrUxDZQ2avPvToaEEawlI5DCcGvroctlsKXUIsRNnbPq8iDJRFnwJFQL`);
                const opdbData = await opdbResponse.json();
                image = opdbData.images?.[0]?.urls?.large || '';
              } catch (error) {
                console.error(`Error fetching from OPDB for ${machine.name}:`, error);
              }
            }

            if (!image && (imageSource === 'ipdb' || imageSource === 'both')) {
              // Construct the image URL using the IPDB ID
              if (machine.ipdb_id) {
                image = `https://www.ipdb.org/images/${machine.ipdb_id}/image-1.png`;
              }
            }

            if (!image) {
              // Use a placeholder image if no image is found
              image = 'https://via.placeholder.com/640x472?text=No+Image+Available';
            }

            return {
              ...machine,
              image,
            };
          })
        );

        // Sort the machines by manufacturer
        machineDetails.sort((a, b) => a.manufacturer.localeCompare(b.manufacturer));

        setMachines(machineDetails);
        setCount(machineDetails.length);
        setLoading(false);
      } catch (error) {
        console.error('Error:', error);
        setError(error.message);
        setLoading(false);
      }
    };

    fetchMachineDetails();
  }, [imageSource]);

  // Function to sort machines based on the selected criteria
  const sortMachines = (machineList, criteria, order) => {
    const sorted = [...machineList].sort((a, b) => {
      let comparison = 0;
      switch (criteria) {
        case 'name':
          comparison = a.name.localeCompare(b.name);
          break;
        case 'year':
          comparison = b.year - a.year; // Default newest first
          break;
        case 'manufacturer':
        default:
          comparison = a.manufacturer.localeCompare(b.manufacturer);
          break;
      }
      return order === 'desc' ? -comparison : comparison;
    });
    return sorted;
  };

  // Group machines by manufacturer if that's the selected sort
  const groupMachinesByManufacturer = (machineList, order) => {
    const grouped = {};
    machineList.forEach(machine => {
      if (!grouped[machine.manufacturer]) {
        grouped[machine.manufacturer] = [];
      }
      grouped[machine.manufacturer].push(machine);
    });
    
    // Sort groups by manufacturer name
    const sortedGroups = {};
    Object.keys(grouped)
      .sort((a, b) => order === 'desc' ? b.localeCompare(a) : a.localeCompare(b))
      .forEach(key => {
        sortedGroups[key] = grouped[key];
      });
    
    return sortedGroups;
  };

  // Group machines by 20-year chunks if year is selected
  const groupMachinesByYear = (machineList, order) => {
    const grouped = {};
    machineList.forEach(machine => {
      const year = machine.year || 0;
      const decade = Math.floor(year / 10) * 10;
      const decadeLabel = `${decade}'s`;
      
      if (!grouped[decadeLabel]) {
        grouped[decadeLabel] = [];
      }
      grouped[decadeLabel].push(machine);
    });
    
    // Sort groups by decade
    const sortedGroups = {};
    Object.keys(grouped)
      .sort((a, b) => {
        const yearA = parseInt(a.split("'")[0]);
        const yearB = parseInt(b.split("'")[0]);
        return order === 'desc' ? yearA - yearB : yearB - yearA;
      })
      .forEach(key => {
        sortedGroups[key] = grouped[key];
      });
    
    return sortedGroups;
  };

  // Sort machines whenever sortBy changes
  const sortedMachines = sortMachines(machines, sortBy, sortOrder);
  const groupedMachines = sortBy === 'manufacturer' ? groupMachinesByManufacturer(sortedMachines, sortOrder) : 
                         sortBy === 'year' ? groupMachinesByYear(sortedMachines, sortOrder) : 
                         null;

  const handleSortChange = (criteria) => {
    if (sortBy === criteria) {
      // Same button clicked, toggle order
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      // Different button clicked, change criteria and reset to default order
      setSortBy(criteria);
      // Set default order based on criteria
      if (criteria === 'year') {
        setSortOrder('desc'); // Newest first for years
      } else {
        setSortOrder('asc'); // A-Z for name and manufacturer
      }
    }
  };

  const openModal = (machine) => {
    setSelectedMachine(machine);
  };

  const closeModal = () => {
    setSelectedMachine(null);
  };

  return (
    <div className="machine-list-container">
      {loading ? (
        <p>Loading...</p>
      ) : error ? (
        <p className="machine-error">
          Couldn't load the machine list right now. Check out our current lineup at{' '}
          <a href="https://pinballmap.com/map?by_location_id=23022" target="_blank" rel="noreferrer">
            Pinball Map
          </a>.
        </p>
      ) : (
        <>
          <h2 className="machine-count">{count} Pinball Machines</h2>
          
          {/* Sort buttons */}
          <div className="sort-controls">
            <span className="sort-label">Sort By:</span>
            <button 
              className={`sort-button ${sortBy === 'name' ? 'active' : ''}`}
              onClick={() => handleSortChange('name')}
            >
              Name {sortBy === 'name' ? (sortOrder === 'asc' ? '↑' : '↓') : ''}
            </button>
            <button 
              className={`sort-button ${sortBy === 'year' ? 'active' : ''}`}
              onClick={() => handleSortChange('year')}
            >
              Year {sortBy === 'year' ? (sortOrder === 'asc' ? '↑' : '↓') : ''}
            </button>
            <button 
              className={`sort-button ${sortBy === 'manufacturer' ? 'active' : ''}`}
              onClick={() => handleSortChange('manufacturer')}
            >
              Manufacturer {sortBy === 'manufacturer' ? (sortOrder === 'asc' ? '↑' : '↓') : ''}
            </button>
          </div>

          {/* Conditional rendering based on sort type */}
          {sortBy === 'manufacturer' ? (
            // Grouped by manufacturer
            <div className="machine-groups">
              {Object.entries(groupedMachines).map(([manufacturer, machineList]) => (
                <div key={manufacturer} className="manufacturer-group">
                  <h3 className="manufacturer-header">{manufacturer}</h3>
                  <div className="machine-grid">
                    {machineList.map((machine) => (
                      <div key={machine.id} className="machine-tile" onClick={() => openModal(machine)}>
                        {machine.image ? (
                          <img src={machine.image} alt={machine.name} className="machine-image" />
                        ) : (
                          <div className="no-image">No Image Available</div>
                        )}
                        <div className="machine-info">
                          <h3>{machine.name}</h3>
                          <p>{machine.manufacturer} ({machine.year})</p>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          ) : sortBy === 'year' ? (
            // Grouped by year ranges
            <div className="machine-groups">
              {Object.entries(groupedMachines).map(([yearRange, machineList]) => (
                <div key={yearRange} className="manufacturer-group">
                  <h3 className="manufacturer-header">{yearRange}</h3>
                  <div className="machine-grid">
                    {machineList.map((machine) => (
                      <div key={machine.id} className="machine-tile" onClick={() => openModal(machine)}>
                        {machine.image ? (
                          <img src={machine.image} alt={machine.name} className="machine-image" />
                        ) : (
                          <div className="no-image">No Image Available</div>
                        )}
                        <div className="machine-info">
                          <h3>{machine.name}</h3>
                          <p>{machine.manufacturer} ({machine.year})</p>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          ) : (
            // Regular grid layout for name sorting
            <div className="machine-grid">
              {sortedMachines.map((machine) => (
                <div key={machine.id} className="machine-tile" onClick={() => openModal(machine)}>
                  {machine.image ? (
                    <img src={machine.image} alt={machine.name} className="machine-image" />
                  ) : (
                    <div className="no-image">No Image Available</div>
                  )}
                  <div className="machine-info">
                    <h3>{machine.name}</h3>
                    <p>{machine.manufacturer} ({machine.year})</p>
                  </div>
                </div>
              ))}
            </div>
          )}

          <p id="source">
            Machine data from{' '}
            <a href="https://pinballmap.com/map?by_location_id=23022" target="_blank" rel="noreferrer">
              Pinball Map
            </a>
            {' '}&mdash; see something wrong? Update our listing there.
          </p>
        </>
      )}
      <Modal show={!!selectedMachine} onClose={closeModal} machine={selectedMachine} />
    </div>
  );
};

export default GameList;
